import { Signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { untilDestroyed } from '@ngneat/until-destroy';
import { I18NextPipe } from 'angular-i18next';
import { isNil } from 'lodash';
import { EnumHelper, MbsSize, ModalService, ModalSettings } from 'mbs-ui-kit';
import { Period, PeriodDisplay } from 'mbs-ui-shared@models/Period.enum';
import { getTimeMultiplier } from 'mbs-ui-shared@utils/date';
import { combineLatest } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { DefaultRetentionPeriodValues, GFSPolicySettings, RetentionPolicyStepValue } from 'wizards/models/retention-policy-models';
import { CalculatedPropertySchedule, CalculatedPropertyStorage, WizardStepsService } from 'wizards/services/wizard-steps.service';
import { NBFGFSModalComponent } from 'wizards/steps/retention-policy-step/NBF/GFS-modal/GFS-modal.component';
import {
  getGFSPolicyFormGroup,
  isKeepBackupForCorrect,
  monthlyMoreYValidator,
  moreThatRetention,
  weeklyMoreMYValidator
} from './retention-policy-step.utils';

export interface RetentionPolicyStepLogicHook {
  isNBF: boolean;
  isHybrid: Signal<boolean>;
  retentionValue: RetentionPolicyStepValue;

  removeError: (controls: AbstractControl[], error: string) => void;
  toggleFormControls: (controlNames: string[], isEnabled: boolean, stepForm?: FormGroup) => void;
}

export class RetentionPolicyStepLogic {
  protected readonly MbsSize = MbsSize;

  public GFSInfo: Array<string> = null;
  public periodEnumSelectArray = EnumHelper.EnumToSelectIndexesArray(Period, PeriodDisplay);
  public alerts: Record<
    | 'keepBackupFFIAlert'
    | 'keepBackupStorageObjectLockAlert'
    | 'keepBackupFullAlert'
    | 'needMinPeriodAlert'
    | 'intelligentRetention'
    | 'needRetentionForGFSAlert',
    string | boolean
  > = {
    keepBackupFFIAlert: '',
    keepBackupStorageObjectLockAlert: '',
    keepBackupFullAlert: '',
    needMinPeriodAlert: false,
    intelligentRetention: false,
    needRetentionForGFSAlert: false
  };
  public schedule: Partial<CalculatedPropertySchedule> = {
    isSimpleMode: false,
    isAdvancedMode: false,
    isManualMode: false,
    incrementalDataEnabled: true,
    scheduleInfo: {
      simple: '',
      incremental: '',
      full: ''
    },
    incrementalSchedule: null,
    fullSchedule: null
  };
  public storage: Partial<CalculatedPropertyStorage> = {
    name: '',
    minPeriod: null,
    canShowImmutabilityByStorage: false,
    immutabilityPeriodDays: null
  };

  private debounceTime = 300;
  private stepForm: FormGroup;
  private deleteVersionsOlderThanCount: AbstractControl;
  private deleteVersionsOlderThanPeriod: AbstractControl;
  private templates = {
    enabled: this.i18nPipe.transform('shared:common:enabled'),
    disabled: this.i18nPipe.transform('shared:common:disabled'),
    ObjectLock: this.i18nPipe.transform('wizards:retention.objectLock'),
    Keep: this.i18nPipe.transform('wizards:retention.advancedSchedule.keep', { format: 'title' }),
    for: this.i18nPipe.transform('wizards:retention.advancedSchedule.for'),
    week: this.i18nPipe.transform('wizards:common.week'),
    weeks: this.i18nPipe.transform('wizards:common.weeks'),
    Weekly: this.i18nPipe.transform('wizards:common.weekly', { format: 'title' }),
    month: this.i18nPipe.transform('wizards:common.month'),
    months: this.i18nPipe.transform('wizards:common.months'),
    Monthly: this.i18nPipe.transform('wizards:common.monthly', { format: 'title' }),
    year: this.i18nPipe.transform('wizards:common.year'),
    years: this.i18nPipe.transform('wizards:common.years'),
    Yearly: this.i18nPipe.transform('wizards:common.yearly', { format: 'title' })
  };

  private set needMinPeriodAlert(needMinPeriodAlert: boolean) {
    this.alerts = { ...this.alerts, needMinPeriodAlert };

    this.stepService.updateProperty('retention', 'alerts', this.alerts);
  }

  private set intelligentRetention(intelligentRetention: boolean) {
    this.alerts = { ...this.alerts, intelligentRetention };

    this.stepService.updateProperty('retention', 'alerts', this.alerts);
  }

  private set needRetentionForGFSAlert(needRetentionForGFSAlert: boolean) {
    this.alerts = { ...this.alerts, needRetentionForGFSAlert };

    this.stepService.updateProperty('retention', 'alerts', this.alerts);
  }

  private set keepBackupFFIAlert(keepBackupFFIAlert: string) {
    this.alerts = { ...this.alerts, keepBackupFFIAlert };

    this.stepService.updateProperty('retention', 'alerts', this.alerts);
  }

  private set keepBackupFullAlert(keepBackupFullAlert: boolean) {
    this.alerts = { ...this.alerts, keepBackupFullAlert };

    this.stepService.updateProperty('retention', 'alerts', this.alerts);
  }

  private set keepBackupStorageObjectLockAlert(keepBackupStorageObjectLockAlert: string) {
    this.alerts = { ...this.alerts, keepBackupStorageObjectLockAlert };

    this.stepService.updateProperty('retention', 'alerts', this.alerts);
  }

  constructor(
    private stepService: WizardStepsService,
    private modalService: ModalService,
    private i18nPipe: I18NextPipe,
    private hooks: RetentionPolicyStepLogicHook
  ) {}

  getForm(): FormGroup {
    this.stepForm = new FormGroup({
      RetentionUseDefaultSettings: new FormControl(false),
      deleteVersionsOlderThanCount: new FormControl(1, [
        this.hooks?.isNBF ? this.olderThanCountMinCustomValidator.bind(this) : Validators.min(1)
      ]),
      deleteVersionsOlderThanPeriod: new FormControl(),
      GFSPolicySettings: getGFSPolicyFormGroup(this.weeklyMoreMonthlyValidator.bind(this), this.monthlyMoreMonthlyValidator.bind(this)),
      HybridRetentionPolicy: new UntypedFormGroup({
        deleteVersionsOlderThan: new FormControl(true),
        deleteVersionsOlderThanCount: new FormControl(3, [Validators.min(1)]),
        deleteVersionsOlderThanPeriod: new FormControl(2)
      })
    });

    return this.stepForm;
  }

  initEvents(): void {
    this.stepService
      .pipeProperty('schedule')
      .pipe(debounceTime(this.debounceTime), untilDestroyed(this.hooks))
      .subscribe((schedule) => {
        const oldData = JSON.stringify(this.schedule);
        const newDataMap = {
          isSimpleMode: schedule.isSimpleMode,
          isAdvancedMode: schedule.isAdvancedMode,
          isManualMode: schedule.isManualMode,
          incrementalDataEnabled: schedule.incrementalDataEnabled,
          scheduleInfo: schedule.scheduleInfo,
          incrementalSchedule: schedule.incrementalSchedule,
          fullSchedule: schedule.fullSchedule
        };
        const newData = JSON.stringify(newDataMap);

        if (oldData != newData) {
          this.schedule = newDataMap;

          if (this.schedule.isManualMode) {
            this.stepForm.get('GFSPolicySettings').get('enabled').setValue(false);
          }

          if (this.schedule.isSimpleMode) {
            this.stepForm.get('RetentionUseDefaultSettings').setValue(false);
            this.stepForm.get('deleteVersionsOlderThanCount').enable();
            this.stepForm.get('deleteVersionsOlderThanPeriod').enable();

            this.stepForm.get('GFSPolicySettings').get('enabled').setValue(false);

            if (this.hooks.isHybrid()) {
              const hybridForm = this.stepForm.get('HybridRetentionPolicy');

              hybridForm.get('deleteVersionsOlderThan').setValue(true);
              hybridForm.get('deleteVersionsOlderThanCount').enable();
              hybridForm.get('deleteVersionsOlderThanPeriod').enable();
            }
          }

          this.updateGFS();
          this.validateRetention();
          this.stepForm.updateValueAndValidity();
        }
      });

    this.stepService
      .pipeProperty('storage')
      .pipe(debounceTime(this.debounceTime), untilDestroyed(this.hooks))
      .subscribe((storage) => {
        const oldData = JSON.stringify(this.storage);
        const newDataMap = {
          name: storage.name,
          type: storage.type,
          hybridName: storage.hybridName,
          hybridType: storage.hybridType,
          minPeriod: storage.minPeriod,
          canShowImmutabilityByStorage: storage.canShowImmutabilityByStorage,
          immutabilityPeriodDays: storage.immutabilityPeriodDays
        };
        const newData = JSON.stringify(newDataMap);

        if (oldData != newData) {
          const needUpdateMinPeriodAlert = this.storage.name !== storage.name || this.storage.minPeriod !== storage.minPeriod;
          this.storage = newDataMap;

          if (!this.storage.canShowImmutabilityByStorage) {
            this.stepForm.get('GFSPolicySettings').get('enabledImmutability').setValue(false);
            this.updateGFSInfo();
          }

          this.stepForm.updateValueAndValidity();
          this.validateRetention();

          if (needUpdateMinPeriodAlert) {
            this.updateMinPeriodAlert();
          }
        }
      });
  }

  initFormEvents(): void {
    this.deleteVersionsOlderThanCount = this.stepForm.get('deleteVersionsOlderThanCount');
    this.deleteVersionsOlderThanPeriod = this.stepForm.get('deleteVersionsOlderThanPeriod');

    combineLatest([this.deleteVersionsOlderThanCount.valueChanges, this.deleteVersionsOlderThanPeriod.valueChanges]).subscribe(() => {
      this.validateRetention();
    });
  }

  initIsHybrid(isHybrid: Signal<boolean>): void {
    toObservable(isHybrid)
      .pipe(untilDestroyed(this.hooks))
      .subscribe({
        next: (isHybrid) => {
          this.stepForm.get('HybridRetentionPolicy')?.[isHybrid ? 'enable' : 'disable']();
          const hybridValue = this.stepForm?.get('HybridRetentionPolicy')?.value;
          const needSetDefault = isNil(hybridValue?.deleteVersionsOlderThan);

          if (isHybrid) {
            if (needSetDefault) {
              this.stepForm.get('HybridRetentionPolicy').setValue({
                deleteVersionsOlderThan: true,
                deleteVersionsOlderThanCount: 3,
                deleteVersionsOlderThanPeriod: 2
              });
            }

            this.stepForm.get('GFSPolicySettings').get('enabled').setValue(false);
            this.offGFSFields(['weekly', 'monthly', 'yearly']);
          }

          this.deleteVersionsOlderThanHybridChangeHandler(isHybrid && (needSetDefault || hybridValue?.deleteVersionsOlderThan));
        }
      });
  }

  getUpdatedValue(value: RetentionPolicyStepValue): RetentionPolicyStepValue {
    const goodKeepBackup = !this.alerts.keepBackupFFIAlert && !this.alerts.keepBackupStorageObjectLockAlert;

    this.needRetentionForGFSAlert = value?.RetentionUseDefaultSettings && value?.GFSPolicySettings.enabled;
    this.stepService.updateProperty('retention', 'isGFSMode', value.GFSPolicySettings.enabled);

    return { ...value, valid: this.stepForm.valid && !this.alerts.needRetentionForGFSAlert && goodKeepBackup };
  }

  updateForm(value: RetentionPolicyStepValue): void {
    const newValue = value;
    const specialFields = ['deleteVersionsOlderThanCount', 'deleteVersionsOlderThanPeriod'];

    this.intelligentRetention = value?.intelligentRetention;

    if (this.hooks.isNBF && value.RetentionUseDefaultSettings) {
      value.deleteVersionsOlderThanCount = DefaultRetentionPeriodValues.deleteVersionsOlderThanCount;
      value.deleteVersionsOlderThanPeriod = DefaultRetentionPeriodValues.deleteVersionsOlderThanPeriod;
    }

    if (this.hooks.isNBF && value.HybridRetentionPolicy && !value.HybridRetentionPolicy.deleteVersionsOlderThan) {
      value.HybridRetentionPolicy = { ...value.HybridRetentionPolicy, ...DefaultRetentionPeriodValues };
    }

    this.stepForm.reset(newValue);

    this.updateGFSInfo();
    this.hooks.toggleFormControls(specialFields, !this.stepForm.get('RetentionUseDefaultSettings').value);
  }

  deleteVersionsOlderThanHybridChangeHandler(state: boolean): void {
    const actionName = state ? 'enable' : 'disable';
    const hybridForm = this.stepForm.get('HybridRetentionPolicy');

    hybridForm.get('deleteVersionsOlderThanCount')[actionName]();
    hybridForm.get('deleteVersionsOlderThanPeriod')[actionName]();
  }

  retentionUseDefaultSettingsChangeHandler(): void {
    queueMicrotask(() => {
      const value = this.stepForm.get('RetentionUseDefaultSettings').value;

      this.stepForm.get('RetentionUseDefaultSettings').setValue(!value);
      this.hooks.toggleFormControls(['deleteVersionsOlderThanCount', 'deleteVersionsOlderThanPeriod'], value);

      if (!value) {
        this.stepForm.get('GFSPolicySettings').get('enabled').setValue(false);

        this.updateGFSInfo();
      }
    });
  }

  retentionHybridChangeHandler(): void {
    this.stepForm.get('deleteVersionsOlderThanCount').markAsDirty();
    this.stepForm.get('deleteVersionsOlderThanCount').markAsTouched();
    this.stepForm.get('deleteVersionsOlderThanCount').updateValueAndValidity();
  }

  retentionChangeHandler(): void {
    this.stepForm.get('deleteVersionsOlderThanCount').updateValueAndValidity();
  }

  GFSEnabledChangeHandler(): Promise<boolean> {
    let resolve: (a: boolean) => void;
    const result = new Promise<boolean>((res) => (resolve = res));
    const newValue = !this.stepForm.get('GFSPolicySettings').get('enabled').value;

    this.stepForm.get('GFSPolicySettings').get('enabled').setValue(newValue);

    if (newValue) {
      queueMicrotask(() => this.clickDetailsGFS(true).then(() => resolve(this.stepForm.get('GFSPolicySettings').get('enabled').value)));
    }

    this.updateGFSInfo();
    this.GFSValidator();

    return result;
  }

  clickDetailsGFS(needReset?: boolean): Promise<boolean> {
    const final = () => {
      if (needReset) {
        this.stepForm.get('GFSPolicySettings').get('enabled').setValue(false);

        this.updateGFSInfo();
      }
    };
    const settings: ModalSettings = {
      ...new ModalSettings(),
      size: MbsSize.sm,
      data: {
        schedule: this.schedule,
        canShowImmutabilityByStorage: this.storage.canShowImmutabilityByStorage,
        GFS: this.stepForm.get('GFSPolicySettings').value,
        retention: {
          count: this.stepForm.get('deleteVersionsOlderThanCount').value,
          period: this.stepForm.get('deleteVersionsOlderThanPeriod').value
        },
        storageObjectLock: {
          count: this.storage.immutabilityPeriodDays,
          period: Period.day
        }
      }
    };

    return this.modalService
      .openCustom(NBFGFSModalComponent, settings)
      .then((result: { data: GFSPolicySettings }) => {
        if (!result) return void final();

        const weekly = result.data.weekly.enabled;
        const monthly = result.data.monthly.enabled;
        const yearly = result.data.yearly.enabled;

        this.stepForm.get('GFSPolicySettings').reset({ ...result.data, enabled: weekly || monthly || yearly });
        this.updateGFSInfo();
        this.GFSValidator();
      })
      .catch(final);
  }

  private olderThanCountMinCustomValidator(control: AbstractControl): ValidationErrors | null {
    const count = control?.value || 0;
    const hybrid = this.stepForm?.value?.HybridRetentionPolicy;

    if (!hybrid?.deleteVersionsOlderThan) return count >= 1 ? null : { minError: true };

    const period = this.stepForm.get('deleteVersionsOlderThanPeriod').value;
    const hybridCount = hybrid.deleteVersionsOlderThanCount;
    const hybridPeriod = hybrid.deleteVersionsOlderThanPeriod;

    return count * getTimeMultiplier(period) < hybridCount * getTimeMultiplier(hybridPeriod) ? { minHybridError: true } : null;
  }

  private weeklyMoreMonthlyValidator(control: AbstractControl): ValidationErrors | null {
    return weeklyMoreMYValidator(this.stepForm?.get('GFSPolicySettings'), control);
  }

  private monthlyMoreMonthlyValidator(control: AbstractControl): ValidationErrors | null {
    return monthlyMoreYValidator(this.stepForm?.get('GFSPolicySettings'), control);
  }

  private validateRetention(): void {
    this.keepBackupForStorageObjectLockValidator();
    this.keepBackupForIncrementalValidator();
    this.keepBackupForFullValidator();
    this.GFSValidator();

    if (this.storage.minPeriod) {
      this.updateMinPeriodAlert();
    }
  }

  private keepBackupForStorageObjectLockValidator(): void {
    const final = () => {
      this.hooks.removeError([this.deleteVersionsOlderThanCount, this.deleteVersionsOlderThanPeriod], 'storageObjectLock');

      return void (this.keepBackupStorageObjectLockAlert = null);
    };

    if (!this.storage.immutabilityPeriodDays) {
      final();
    }

    const selected: { count: number; period: Period } = {
      count: this.deleteVersionsOlderThanCount.value,
      period: this.deleteVersionsOlderThanPeriod.value
    };

    const hasError = moreThatRetention({ count: this.storage.immutabilityPeriodDays, period: Period.day }, selected, false);

    if (hasError) {
      this.keepBackupStorageObjectLockAlert = this.i18nPipe.transform('wizards:retention.keepBackupStorageObjectLockErrorMessage', {
        count: this.storage.immutabilityPeriodDays,
        period: this.periodEnumSelectArray[Period.day].label.toLocaleString().toLowerCase()
      });

      this.deleteVersionsOlderThanCount.setErrors({ storageObjectLock: true });
      this.deleteVersionsOlderThanPeriod.setErrors({ storageObjectLock: true });

      this.hooks.retentionValue = { ...this.hooks.retentionValue, valid: false };

      return;
    }

    final();
  }

  /**
   * Depends of Schedule Step data
   * @return {null}
   */
  private keepBackupForIncrementalValidator(): void {
    if (!this.schedule.isSimpleMode || !this.schedule.incrementalSchedule) {
      this.hooks.removeError([this.deleteVersionsOlderThanCount, this.deleteVersionsOlderThanPeriod], 'keepBackupPeriodInvalid');

      return void (this.keepBackupFFIAlert = null);
    }

    const selected: { count: number; period: Period } = {
      count: this.deleteVersionsOlderThanCount.value,
      period: this.deleteVersionsOlderThanPeriod.value
    };

    const incrementalKeepBackupResult = isKeepBackupForCorrect(this.schedule.incrementalSchedule, selected);

    if (!incrementalKeepBackupResult.error) {
      this.hooks.removeError([this.deleteVersionsOlderThanCount, this.deleteVersionsOlderThanPeriod], 'keepBackupPeriodInvalid');

      return void (this.keepBackupFFIAlert = null);
    }

    this.keepBackupFFIAlert = this.i18nPipe.transform('wizards:retention.keepBackupFFIErrorMessage', {
      count: incrementalKeepBackupResult.count,
      period: this.periodEnumSelectArray[incrementalKeepBackupResult.period].label.toLocaleString().toLowerCase().split('(')[0]
    });

    this.deleteVersionsOlderThanCount.setErrors({ keepBackupPeriodInvalid: true });
    this.deleteVersionsOlderThanPeriod.setErrors({ keepBackupPeriodInvalid: true });

    this.hooks.retentionValue = { ...this.hooks.retentionValue, valid: false };
  }

  /**
   * Depends of Schedule Step data
   * @return {null}
   */
  private keepBackupForFullValidator(): void {
    if (this.schedule.isManualMode || this.schedule.isSimpleMode || !this.schedule.isAdvancedMode) {
      return void (this.keepBackupFullAlert = null);
    }

    const selected: { count: number; period: Period } = {
      count: this.deleteVersionsOlderThanCount.value,
      period: this.deleteVersionsOlderThanPeriod.value
    };

    const fullKeepBackupResult = isKeepBackupForCorrect(this.schedule.fullSchedule, selected);

    if (!fullKeepBackupResult.error) {
      return void (this.keepBackupFullAlert = null);
    }

    this.keepBackupFullAlert = true;
  }

  private GFSValidator(): void {
    const enabledGFS = this.stepForm.get('GFSPolicySettings').get('enabled').value;
    const retention = {
      count: this.stepForm.get('deleteVersionsOlderThanCount').value,
      period: this.stepForm.get('deleteVersionsOlderThanPeriod').value
    };
    const storageObjectLock = {
      count: this.storage.immutabilityPeriodDays,
      period: Period.day
    };

    let GFSMoreThatRetention = false;
    let GFSMoreThatStorageObjectLock = false;

    ['weekly', 'monthly', 'yearly'].forEach((key) => {
      let period: Period;
      const controlValue = this.stepForm.get('GFSPolicySettings').get(key).get('storePeriod').value;
      const enabled = this.stepForm.get('GFSPolicySettings').get(key).get('enabled').value;

      if (!enabled || !enabledGFS) {
        return;
      }

      switch (key) {
        case 'weekly':
          period = Period.week;
          break;
        case 'monthly':
          period = Period.month;
          break;
        case 'yearly':
          period = Period.year;
          break;
        default:
          break;
      }

      GFSMoreThatRetention = GFSMoreThatRetention || !moreThatRetention({ count: controlValue, period }, retention);

      if (storageObjectLock.count) {
        GFSMoreThatStorageObjectLock =
          GFSMoreThatStorageObjectLock || !moreThatRetention({ count: controlValue, period }, storageObjectLock);
      }
    });

    this.hooks.removeError([this.deleteVersionsOlderThanCount, this.deleteVersionsOlderThanPeriod], 'GFSMoreThatRetention');
    this.hooks.removeError([this.deleteVersionsOlderThanCount, this.deleteVersionsOlderThanPeriod], 'GFSMoreThatStorageObjectLock');

    if (GFSMoreThatRetention) {
      this.deleteVersionsOlderThanCount.setErrors({ GFSMoreThatRetention: true });
      this.deleteVersionsOlderThanPeriod.setErrors({ GFSMoreThatRetention: true });
    }

    if (GFSMoreThatStorageObjectLock) {
      this.deleteVersionsOlderThanCount.setErrors({ GFSMoreThatStorageObjectLock: true });
      this.deleteVersionsOlderThanPeriod.setErrors({ GFSMoreThatStorageObjectLock: true });
    }
  }

  private updateMinPeriodAlert(): void {
    this.needMinPeriodAlert = false;

    if (!this.storage.minPeriod) return;

    if (this.stepForm.get('RetentionUseDefaultSettings').value) {
      return;
    }

    const count = this.stepForm.get('deleteVersionsOlderThanCount').value;
    const period = this.stepForm.get('deleteVersionsOlderThanPeriod').value;
    let reason: boolean;

    switch (period) {
      case Period.year: {
        reason = false;
        break;
      }
      case Period.day: {
        reason = count < this.storage.minPeriod;
        break;
      }
      case Period.week: {
        reason = count * 7 < this.storage.minPeriod;
        break;
      }
      default: {
        reason = count * 30 < this.storage.minPeriod;
      }
    }

    this.needMinPeriodAlert = reason;
  }

  private updateGFS(): void {
    const scheduleMonthly = this.schedule?.fullSchedule?.recurringPeriod === 'Monthly';
    const scheduleCount = this.schedule?.fullSchedule?.repeatEveryCount || 0;

    if (scheduleMonthly ? scheduleCount < 12 : scheduleCount >= 30) this.offGFSFields(['weekly']);

    if (scheduleMonthly && scheduleCount >= 12) this.offGFSFields(['weekly', 'monthly']);

    this.updateGFSInfo();
  }

  private offGFSFields(names: string[]): void {
    const gfsControl = this.stepForm.get('GFSPolicySettings');

    names.forEach((name: string) => {
      gfsControl.get(name).get('enabled').setValue(false);
    });
  }

  private updateGFSInfo(): void {
    const GFSPolicySettings = this.stepForm.get('GFSPolicySettings').value;

    if (!GFSPolicySettings.enabled) {
      return void delete this.GFSInfo;
    }

    const t = this.templates;
    const weekly = GFSPolicySettings.weekly?.enabled ? GFSPolicySettings.weekly.storePeriod : '';
    const monthly = GFSPolicySettings.monthly?.enabled ? GFSPolicySettings.monthly.storePeriod : '';
    const yearly = GFSPolicySettings.yearly?.enabled ? GFSPolicySettings.yearly.storePeriod : '';
    const keep = weekly || monthly || yearly;
    const objectLock = GFSPolicySettings.enabledImmutability;

    // Weekly for 1 week / Weekly for 2 weeks
    const weeklyString = weekly ? `${t.Weekly} ${t.for} ${weekly} ${weekly > 1 ? t.weeks : t.week}` : '';
    // Monthly for 1 month / Monthly for 2 months
    const monthlyString = monthly ? `${t.Monthly} ${t.for} ${monthly} ${monthly > 1 ? t.months : t.month}` : '';
    // Yearly for 1 year / Yearly for 2 years
    const yearlyString = yearly ? `${t.Yearly} ${t.for} ${yearly} ${yearly > 1 ? t.years : t.year}` : '';

    this.GFSInfo = [];

    if (keep) {
      this.GFSInfo.push(`${t.Keep} ${[weeklyString, monthlyString, yearlyString].filter((s) => !!s).join(', ')}.`);
    }

    this.GFSInfo.push(`${t.ObjectLock}: ${objectLock ? t.enabled : t.disabled}`);
  }
}
